import { useEffect, useState } from "react";
import ModuleContainer from "./ModuleContainer";
import { Bar } from "react-chartjs-2";
import { supabase } from "../../lib/supabase";
import { colors } from "../../lib/designTokens";
import {
  aggregateOrganicBrandMentionsForFrequency,
  normalizeBrandMentionKey
} from "../../lib/brandMentionAggregation";
import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

type Leader = {
  brand: string;
  canonicalKey: string;
  rawNames: string[];
  status: "mapped";
  visibility: number;
  rank: number;
};

type MyRank = {
  brand: string;
  visibility: number;
  rank: number;
};

type ModuleStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

export default function AILeaderboard({
  account,
  customerBrand,
  runId,
}: {
  account?: string;
  customerBrand?: string;
  runId?: string;
}) {
  const [leaders, setLeaders] = useState<Leader[]>([]);
const [myRank, setMyRank] = useState<MyRank | null>(null);
const [status, setStatus] = useState<ModuleStatus>("loading");
const [statusMessage, setStatusMessage] = useState("");
const [sampleSize, setSampleSize] = useState<number | null>(null);
const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  useEffect(() => {
  if (!account || !runId) {
  setLeaders([]);
  setMyRank(null);
  setSampleSize(null);
  setStatus("unavailable");
  setStatusMessage("This module is not available for this run yet.");
  return;
}

  fetchLeaderboard();
}, [account, runId]);

async function fetchLeaderboard() {
  if (!account || !runId) {
    setLeaders([]);
    setMyRank(null);
    setSampleSize(null);
    setStatus("unavailable");
    setStatusMessage("This module is not available for this run yet.");
    return;
  }

  setLeaders([]);
  setMyRank(null);
  setStatus("loading");
  setStatusMessage("");

  const { data: promptRows, error } = await supabase
    .from("prompt_results")
    .select("brand_mentions, visibility_signal, prompt_type, status")
    .eq("account_id", account)
    .eq("run_id", runId)
    .eq("prompt_type", "organic")
    .eq("status", "completed")
    .neq("visibility_signal", "error")
    .limit(1000);

  if (error) {
    console.warn("[AILeaderboard] Lightweight query error", error);
    setLeaders([]);
    setMyRank(null);
    setSampleSize(null);
    setStatus("empty");
    setStatusMessage("Not enough data available yet.");
    return;
  }

  setSampleSize((promptRows || []).length || null);

  const rollup = aggregateOrganicBrandMentionsForFrequency(
    promptRows || [],
    (row: any) => row.brand_mentions,
    customerBrand
  );

  const totalMentions = rollup.reduce((sum, item) => sum + item.count, 0);

  const rankedRows = rollup
    .slice()
    .sort((a, b) => b.count - a.count)
      .map((item, index) => ({
        brand: item.displayName,
        canonicalKey: item.canonicalKey,
        rawNames: item.rawNames,
        status: "mapped" as const,
        visibility: totalMentions > 0
          ? Number(((item.count / totalMentions) * 100).toFixed(2))
          : 0,
      rank: index + 1
    }));

  if (!rankedRows.length) {
    setLeaders([]);
    setMyRank(null);
    setStatus("empty");
    setStatusMessage("Not enough data available yet.");
    return;
  }

  const top10 = rankedRows.slice(0, 10);
  setLeaders(top10);
  setStatus("ready");

  if (customerBrand) {
    const found = rankedRows.find(
      (row) =>
        row.canonicalKey === normalizeBrandMentionKey(customerBrand) ||
        row.rawNames.some(
          (rawName) => normalizeBrandMentionKey(rawName) === normalizeBrandMentionKey(customerBrand)
        )
    );

    setMyRank(found || null);
  }
  }


  function formatVisibility(value: number) {
    return `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
  }

  function getToggleButtonStyle(mode: "chart" | "table"): React.CSSProperties {
    const active = viewMode === mode;

    return {
      border: "1px solid " + (active ? colors.primary : "#dbe3ef"),
      background: active ? colors.primary : "#ffffff",
      color: active ? "#ffffff" : colors.textSecondary,
      borderRadius: 999,
      padding: "6px 12px",
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer"
    };
  }

  function getLeaderboardInsight() {
    if (!leaders.length) return "";

    const top = leaders[0];
    const second = leaders[1];
    const my = myRank;

    if (!top) return "";

    const gap = second ? top.visibility - second.visibility : 0;

    if (my && my.rank === 1) {
      if (gap < 5) {
        return `${my.brand} leads in AI visibility, but competitors are close behind, indicating a highly competitive position.`;
      }
      return `${my.brand} leads in AI visibility with a strong margin, indicating dominant presence in AI-generated results.`;
    }

    if (my && my.rank <= 3) {
      return `${my.brand} ranks among the top brands in AI visibility, but trails the leader, suggesting an opportunity to strengthen presence in key queries.`;
    }

    if (my && my.rank > 3) {
      return `${my.brand} trails leading competitors in AI visibility, indicating an opportunity to increase presence across AI-driven discovery and recommendation.`;
    }

    return `${top.brand} leads in AI visibility, with other competitors competing closely for presence across AI-generated responses.`;
  }

  if (status !== "ready" || !leaders.length) {
  return (
    <ModuleContainer
      title="AI Visibility Leaderboard"
      state={status}
      message={statusMessage}
    />
  );
}

  const isInTop10 = leaders.some(
    (leader) =>
      leader.canonicalKey === normalizeBrandMentionKey(customerBrand || "") ||
      leader.rawNames.some(
        (rawName) => normalizeBrandMentionKey(rawName) === normalizeBrandMentionKey(customerBrand || "")
      )
  );

  const chartData = {
    labels: leaders.map((b) => `#${b.rank} ${b.brand}`),
    datasets: [
  {
    label: "Share of Voice",
    data: leaders.map((b) => b.visibility),
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    borderWidth: 1,
    borderRadius: 3
  }
]
  };


  const tableHeaderStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "10px 12px",
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: "1px solid #dbe3ef",
    background: "#f8fafc"
  };

  const tableCellStyle: React.CSSProperties = {
    padding: "12px",
    color: colors.textPrimary,
    verticalAlign: "middle"
  };

  return (
    <ModuleContainer title="AI Visibility Leaderboard">
      <p
        style={{
          marginBottom: 10,
          color: colors.textSecondary,
          fontSize: 14
        }}
      >
Which brands appear in the most completed organic buyer-journey responses. Each brand counts at most once per response.
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginBottom: 14
        }}
      >
        <button
          type="button"
          onClick={() => setViewMode("chart")}
          style={getToggleButtonStyle("chart")}
        >
          Chart
        </button>
        <button
          type="button"
          onClick={() => setViewMode("table")}
          style={getToggleButtonStyle("table")}
        >
          Table
        </button>
      </div>

      {viewMode === "chart" ? (
      <Bar
        data={chartData}
        options={{
          indexAxis: "y",
          responsive: true,
          plugins: {
  datalabels: {
    anchor: "center",
    align: "center",
    color: colors.textMuted,

    formatter: (value) => `${value.toFixed(2)}%`
  }
}
        }}
      />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 14
            }}
          >
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Rank</th>
                <th style={tableHeaderStyle}>Brand</th>
                <th style={{ ...tableHeaderStyle, textAlign: "right" }}>AI visibility</th>
              </tr>
            </thead>
            <tbody>
              {leaders.map((leader) => {
                const isCustomer =
                  leader.canonicalKey === normalizeBrandMentionKey(customerBrand || "") ||
                  leader.rawNames.some(
                    (rawName) =>
                      normalizeBrandMentionKey(rawName) ===
                      normalizeBrandMentionKey(customerBrand || "")
                  );

                return (
                  <tr
                    key={`${leader.rank}-${leader.brand}`}
                    style={{
                      background: isCustomer ? "#eff6ff" : "#ffffff",
                      borderBottom: "1px solid #e5e7eb"
                    }}
                  >
                    <td style={tableCellStyle}>#{leader.rank}</td>
                    <td
                      style={{
                        ...tableCellStyle,
                        fontWeight: isCustomer ? 800 : 600,
                        color: isCustomer ? colors.primary : colors.textPrimary
                      }}
                    >
                      {leader.brand}
                      {isCustomer ? " · You" : ""}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        textAlign: "right",
                        fontWeight: 800
                      }}
                    >
                      {formatVisibility(leader.visibility)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sampleSize !== null && sampleSize < 18 && (
  <div
    style={{
      marginTop: 10,
      padding: "8px 10px",
      borderRadius: 8,
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      color: "#64748b",
      fontSize: 12,
      lineHeight: 1.4
    }}
  >
    Limited competitive sample. Results are directional and may be more volatile.
  </div>
)}

      {!isInTop10 && myRank && (
        <div
          style={{
            marginTop: 20,
            padding: 16,
            background: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: 10
          }}
        >
          <strong>Your Position</strong>
          <div style={{ marginTop: 6, color: colors.textSecondary }}>
            #{myRank.rank} {myRank.brand}
          </div>
          <div style={{ fontSize: 12, color: colors.textSecondary }}>
            Visibility Score: {myRank.visibility.toFixed(2)}%
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          padding: "12px 14px",
          background: colors.background,
border: `1px solid ${colors.border}`,
          borderRadius: 10
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: colors.textSecondary,
            marginBottom: 4,
            letterSpacing: "0.04em"
          }}
        >
        🧠 LEADERBOARD INSIGHT
        </div>

        <div
          style={{
            fontSize: 13,
            color: colors.textPrimary,
            lineHeight: 1.4
          }}
        >
          {getLeaderboardInsight()}
        </div>
      </div>
    </ModuleContainer>
  );
}
